import React, { useState, useEffect, useMemo, useRef } from 'react';

// Utility function to generate unique IDs
const generateUniqueId = (): string => {
  return `member-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Utility function for deep comparison of form data
const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
  if (obj1 === obj2) return true;
  
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
  
  const keys1 = Object.keys(obj1 as Record<string, unknown>);
  const keys2 = Object.keys(obj2 as Record<string, unknown>);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])) return false;
  }
  
  return true;
};

import { createPortal } from 'react-dom';
import type { GroupMember, GroupMemberBeneficiary } from '../../../types/customer-portal';
import DatePicker from '../../../../../shared/components/ui/DatePicker/DatePicker';
import PremiumUpdateModal from '../../../../../shared/components/ui/PremiumUpdateModal';
import { dateUtils } from '../../../../../shared/utils/dateUtils';
import {
  COUNTRY_CODES,
  getDefaultCountryCode,
  autoFormatPhoneNumber,
  validatePhoneNumber,
  getCleanPhoneNumber
} from '../../../../../shared/utils/phoneUtils';
import SearchableDropdown from '../../../../../components/common/SearchableDropdown';
import { isValidPhilippineZipCode } from '../../../../../data/mockLocationData';
import './GroupMemberModal.css';
import '../../../styles/zipCodeAutoPopulation.css';
import { validateIDNumber, formatIDNumber, getIDFormatInfo } from '../../../../../shared/utils/idFormatValidation';

import { useRelationships } from '@/portals/customer/hooks/useRelationships';
import { useIDTypes } from '@/portals/customer/hooks/useIDTypes';
import { useUpdatedPremiumPricebyAge } from '@/portals/customer/hooks/useUpdatedPremiumPricebyAge';
import { usePlanSelection } from '@/portals/customer/contexts/PlanSelectionContext';
import { useLocalAddressesProvinces } from '@/portals/customer/hooks/useLocalAddressesProvinces';
import { useLocalAddressesCities } from '@/portals/customer/hooks/useLocalAddressesCities';
import { useLocalAddressesBarangays } from '@/portals/customer/hooks/useLocalAddressesBarangays';
import { getProvinceCodeFromDescription } from '../../../utils/provinceMapper';
import { getZipCodeFromBarangay, shouldAutoPopulateZipCode } from '../../../utils/barangayZipCodeUtils';

interface GroupMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: GroupMember) => void;
  onDelete?: (memberId: string) => void;
  member?: GroupMember | null;
  isEditMode?: boolean;
  isFirstMember?: boolean;
  firstMemberAddress?: {
    houseUnitNo?: string;
    streetName?: string;
    subdivisionVillage?: string;
    barangay?: string;
    city?: string;
    province?: string;
    zipCode?: string;
    country?: string;
  } | null;
  shareAddressEnabled?: boolean;
}

const GroupMemberModal: React.FC<GroupMemberModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  member,
  isEditMode = false,
  isFirstMember = false,
  firstMemberAddress = null,
  shareAddressEnabled = false
}) => {
  const [formData, setFormData] = useState<Omit<GroupMember, 'id'>>({
    title: 'Ms.',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: 'N/A',
    dateOfBirth: '',
    age: '',
    gender: '',
    occupation: '',
    nationality: 'Filipino',
    civilStatus: '',
    shareAddressWithGroupMembers: false,
    houseUnitNo: '',
    streetName: '',
    subdivisionVillage: '',
    barangay: '',
    city: '',
    province: '',
    zipCode: '',
    country: 'Philippines',
    contactType: 'mobile',
    countryCode: '+63',
    phoneNumber: '',
    formattedPhoneNumber: '',
    contactNumber: '', // Legacy field
    // Add landline fields
    landlineCountryCode: '+63',
    landlineNumber: '',
    formattedLandlineNumber: '',
    idType: 'Driver\'s License',
    idNumber: '',
    beneficiaries: []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  // ID number validation state
  const [idNumberError, setIdNumberError] = useState<string | null>(null);
  const [isIdNumberTouched, setIsIdNumberTouched] = useState(false);
  // Zip code auto-population state
  const [isZipCodeAutoPopulated, setIsZipCodeAutoPopulated] = useState(false);

  // Use the custom hook to fetch relationships
  const { relationshipsOptions, loading: relationshipsLoading, error: relationshipsError } = useRelationships();
  // Use the custom hook to fetch valid IDs or ID Types
  const { idTypesOptions, loading: idTypesLoading, error: idTypesError } = useIDTypes();
  // Use the local addresses provinces hook
  const { provinces, provinceOptions, loading: provincesLoading, error: provincesError } = useLocalAddressesProvinces();
  
  // Get selected province code for city filtering
  const selectedProvinceCode = useMemo(() => {
    return getProvinceCodeFromDescription(provinces, formData.province || '');
  }, [provinces, formData.province]);

  // Use the local addresses cities hook with province filtering
  const { cityOptions, loading: citiesLoading, error: citiesError } = useLocalAddressesCities(selectedProvinceCode);
  
  // Use the local addresses barangays hook with city filtering
  const { barangays, barangayOptions, loading: barangaysLoading, error: barangaysError } = useLocalAddressesBarangays(formData.city);
  
  // Track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState<Omit<GroupMember, 'id'> | null>(null);

  // Premium modal state management
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [hasShownModalForAge, setHasShownModalForAge] = useState<number | null>(null);

  // Country dropdown states
  const [isMobileCountryDropdownOpen, setIsMobileCountryDropdownOpen] = useState(false);
  const [isLandlineCountryDropdownOpen, setIsLandlineCountryDropdownOpen] = useState(false);

  // Refs for contact functionality
  const mobileCountryDropdownRef = useRef<HTMLDivElement>(null);
  const landlineCountryDropdownRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const landlineInputRef = useRef<HTMLInputElement>(null);

  // PABA API hook and plan selection context - UPDATED
  const { 
    adjustedPrice, 
    loading: pabaLoading, 
    calculatePremiumByAge,
    clearCalculation 
  } = useUpdatedPremiumPricebyAge();
  
  const { selectedPlan, setGroupMemberAdjustment, isShortSecure, getTempDataValue } = usePlanSelection();

  // Get tempData values from context (same as PrincipalInsuredStep) - UPDATED
  const tempDataRateAmount = getTempDataValue('rateAmount') as number | null;
  const tempDataCurrencyCode = getTempDataValue('currencyCode') as string | null;
  
  console.log("GroupMemberModal: tempData values:", {
    rateAmount: tempDataRateAmount,
    currencyCode: tempDataCurrencyCode,
    selectedPlanRateAmount: selectedPlan?.rate?.rateAmount,
    selectedPlanExists: !!selectedPlan
  });

  // Store premium adjustment in context when calculated (skip for Short Secure plans)
  useEffect(() => {
    if (!isShortSecure() && adjustedPrice && formData.age) {
      const currentAge = parseInt(formData.age);
      const currentPremiumAmount = getCurrentPremiumAmount();
      
      if (currentAge >= 76 && currentAge <= 100 && adjustedPrice !== currentPremiumAmount) {
        // For new members, use a temporary ID based on form data
        const memberId = member?.id || `temp-${formData.firstName}-${formData.lastName}-${currentAge}`;
        
        console.log('GroupMemberModal: Storing premium adjustment:', {
          memberId,
          originalPrice: currentPremiumAmount,
          adjustedPrice,
          age: currentAge,
          isNewMember: !member?.id
        });
        
        setGroupMemberAdjustment(memberId, {
          memberId,
          originalPrice: currentPremiumAmount,
          adjustedPrice,
          age: currentAge
        });
      }
    }
  }, [adjustedPrice, member?.id, formData.age, formData.firstName, formData.lastName, setGroupMemberAdjustment, isShortSecure]);

  // Create birthdate validation rules - use Short Secure rules (0-75) or standard rules (18-100)
  const birthdateValidationRules = useMemo(() => {
    return isShortSecure() ? dateUtils.createShortSecureBirthdateRules() : dateUtils.createBirthdateRules();
  }, [isShortSecure]);

  // Get current country code info for mobile
  const currentMobileCountryCode = useMemo(() => {
    return COUNTRY_CODES.find(cc => cc.value === formData.countryCode) || getDefaultCountryCode();
  }, [formData.countryCode]);

  // Get current country code info for landline
  const currentLandlineCountryCode = useMemo(() => {
    return COUNTRY_CODES.find(cc => cc.value === formData.landlineCountryCode) || getDefaultCountryCode();
  }, [formData.landlineCountryCode]);

  // Get ID format info for the currently selected ID type
  const idFormatInfo = useMemo(() => {
    return formData.idType ? getIDFormatInfo(formData.idType) : null;
  }, [formData.idType]);

  // Validate ID number whenever idType or idNumber changes
  const idValidationResult = useMemo(() => {
    if (!isIdNumberTouched || !formData.idNumber.trim()) {
      return { isValid: true };
    }
    return validateIDNumber(formData.idType, formData.idNumber);
  }, [formData.idType, formData.idNumber, isIdNumberTouched]);

  // Check if form data has changed from original
  const hasFormChanged = useMemo(() => {
    if (!isEditMode || !originalFormData) return true; // Always allow save for new members
    return !deepEqual(formData, originalFormData);
  }, [formData, originalFormData, isEditMode]);

  // Check if delete button should be shown
  const showDeleteButton = isEditMode && member && onDelete;

  // Get current premium amount for PABA calculation - UPDATED WITH ENHANCED LOGIC
  const getCurrentPremiumAmount = (): number => {
    console.log('GroupMemberModal: Premium calculation debug');
    
    // Enhanced priority logic with better validation (same as PrincipalInsuredStep)
    let currentPremiumAmount = 29; // Final fallback
    
    // Priority 1: selectedPlan rate amount (with proper validation)
    if (selectedPlan?.rate?.rateAmount &&
        typeof selectedPlan.rate.rateAmount === 'number' &&
        selectedPlan.rate.rateAmount > 0 &&
        !isNaN(selectedPlan.rate.rateAmount)) {
      currentPremiumAmount = selectedPlan.rate.rateAmount;
      console.log('GroupMemberModal: Using selectedPlan rateAmount:', currentPremiumAmount);
    }
    // Priority 2: tempData rate amount (with enhanced validation)
    else if (tempDataRateAmount !== null &&
             tempDataRateAmount !== undefined &&
             typeof tempDataRateAmount === 'number' &&
             tempDataRateAmount > 0 &&
             !isNaN(tempDataRateAmount)) {
      currentPremiumAmount = tempDataRateAmount;
      console.log('GroupMemberModal: Using tempData rateAmount as fallback:', currentPremiumAmount);
    }
    // Priority 3: Final fallback
    else {
      console.log('GroupMemberModal: No valid rateAmount found, using final fallback:', currentPremiumAmount);
      console.log('GroupMemberModal: Debug - selectedPlan?.rate?.rateAmount:', selectedPlan?.rate?.rateAmount);
      console.log('GroupMemberModal: Debug - tempDataRateAmount:', tempDataRateAmount);
    }
    
    return currentPremiumAmount;
  };

  // Reset form when modal opens/closes or member changes
  useEffect(() => {
    if (isOpen) {
      if (member && isEditMode) {
        const memberFormData = {
          title: member.title,
          firstName: member.firstName,
          middleName: member.middleName || '',
          lastName: member.lastName,
          suffix: member.suffix,
          dateOfBirth: member.dateOfBirth,
          age: member.age,
          gender: member.gender,
          occupation: member.occupation,
          nationality: member.nationality,
          civilStatus: member.civilStatus,
          shareAddressWithGroupMembers: member.shareAddressWithGroupMembers || false,
          houseUnitNo: member.houseUnitNo || '',
          streetName: member.streetName || '',
          subdivisionVillage: member.subdivisionVillage || '',
          barangay: member.barangay || '',
          city: member.city || '',
          province: member.province || '',
          zipCode: member.zipCode || '',
          country: member.country || 'Philippines',
          contactType: member.contactType || 'mobile',
          countryCode: member.countryCode || '+63',
          phoneNumber: member.phoneNumber || '',
          formattedPhoneNumber: member.formattedPhoneNumber || '',
          contactNumber: member.contactNumber,
          landlineCountryCode: member.landlineCountryCode || '+63',
          landlineNumber: member.landlineNumber || '',
          formattedLandlineNumber: member.formattedLandlineNumber || '',
          idType: member.idType,
          idNumber: member.idNumber,
          beneficiaries: member.beneficiaries || []
        };
        setFormData(memberFormData);
        // Store original data for change detection
        setOriginalFormData(JSON.parse(JSON.stringify(memberFormData)));
      } else {
        // For new members, check if we should auto-populate address from first member
        const shouldAutoPopulateAddress = !isFirstMember && shareAddressEnabled && firstMemberAddress;
        
        const newMemberFormData = {
          title: 'Ms.' as const,
          firstName: '',
          middleName: '',
          lastName: '',
          suffix: 'N/A',
          dateOfBirth: '',
          age: '',
          gender: '',
          occupation: '',
          nationality: 'Filipino',
          civilStatus: '',
          shareAddressWithGroupMembers: false, // Only first member can set this
          houseUnitNo: shouldAutoPopulateAddress ? (firstMemberAddress.houseUnitNo || '') : '',
          streetName: shouldAutoPopulateAddress ? (firstMemberAddress.streetName || '') : '',
          subdivisionVillage: shouldAutoPopulateAddress ? (firstMemberAddress.subdivisionVillage || '') : '',
          barangay: shouldAutoPopulateAddress ? (firstMemberAddress.barangay || '') : '',
          city: shouldAutoPopulateAddress ? (firstMemberAddress.city || '') : '',
          province: shouldAutoPopulateAddress ? (firstMemberAddress.province || '') : '',
          zipCode: shouldAutoPopulateAddress ? (firstMemberAddress.zipCode || '') : '',
          country: shouldAutoPopulateAddress ? (firstMemberAddress.country || 'Philippines') : 'Philippines',
          contactType: 'mobile' as 'mobile' | 'landline',
          countryCode: '+63',
          phoneNumber: '',
          formattedPhoneNumber: '',
          contactNumber: '',
          landlineCountryCode: '+63',
          landlineNumber: '',
          formattedLandlineNumber: '',
          idType: 'Driver\'s License',
          idNumber: '',
          beneficiaries: []
        };
        setFormData(newMemberFormData);
        setOriginalFormData(null); // No original data for new members
      }
      setErrors({});
      // Reset ID validation state when modal opens
      setIdNumberError(null);
      setIsIdNumberTouched(false);
      // Clear any previous PABA calculations
      clearCalculation();
    }
  }, [isOpen, member, isEditMode, isFirstMember, shareAddressEnabled, firstMemberAddress, clearCalculation]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileCountryDropdownRef.current && !mobileCountryDropdownRef.current.contains(event.target as Node)) {
        setIsMobileCountryDropdownOpen(false);
      }
      if (landlineCountryDropdownRef.current && !landlineCountryDropdownRef.current.contains(event.target as Node)) {
        setIsLandlineCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle dynamic address updates for subsequent members when first member's address changes
  useEffect(() => {
    // Only update if modal is open, not in edit mode, not first member, and address sharing is enabled
    if (isOpen && !isEditMode && !isFirstMember && shareAddressEnabled && firstMemberAddress) {
      console.log('GroupMemberModal - Address sharing enabled, updating form data dynamically');
      setFormData(prev => ({
        ...prev,
        houseUnitNo: firstMemberAddress.houseUnitNo || '',
        streetName: firstMemberAddress.streetName || '',
        subdivisionVillage: firstMemberAddress.subdivisionVillage || '',
        barangay: firstMemberAddress.barangay || '',
        city: firstMemberAddress.city || '',
        province: firstMemberAddress.province || '',
        zipCode: firstMemberAddress.zipCode || '',
        country: firstMemberAddress.country || 'Philippines'
      }));
    }
  }, [
    firstMemberAddress?.houseUnitNo,
    firstMemberAddress?.streetName,
    firstMemberAddress?.subdivisionVillage,
    firstMemberAddress?.barangay,
    firstMemberAddress?.city,
    firstMemberAddress?.province,
    firstMemberAddress?.zipCode,
    firstMemberAddress?.country,
    isOpen,
    isEditMode,
    isFirstMember,
    shareAddressEnabled
  ]);

  const handleInputChange = (field: keyof Omit<GroupMember, 'id'>, value: string | boolean) => {
    let processedValue: string | boolean = value;

    // Prevent country field changes - always keep it as Philippines
    if (field === 'country') {
      return;
    }

    // Handle address sharing checkbox logic
    if (field === 'shareAddressWithGroupMembers') {
      // First member can always toggle the checkbox
      // Subsequent members can toggle for visual feedback but actual sharing is controlled by first member
      processedValue = value;
    }

    // Handle ID number formatting and validation
    if (field === 'idNumber' && typeof value === 'string') {
      setIsIdNumberTouched(true);
      // Format the ID number based on the selected ID type
      if (formData.nationality === "Filipino" || formData.nationality === "") {
        // Format the ID number based on the selected ID type
        if (formData.idType) {
          processedValue = formatIDNumber(formData.idType, value);
        }
      }
    }

    // Clear ID number when ID type changes
    if (field === 'idType') {
      setFormData(prev => ({
        ...prev,
        [field]: processedValue as string,
        idNumber: '' // Clear ID number when type changes
      }));
      setIsIdNumberTouched(false);
      setIdNumberError(null);
      return;
    }

    if (field === 'nationality') {
      setFormData(prev => ({
        ...prev,
        [field]: processedValue as string,
        idNumber: '', // Clear ID number when type changed
        idType: '', // clear ID type when nationality changed
      }));
      setIsIdNumberTouched(false);
      setIdNumberError(null);
      return;
    }

    setFormData(prev => {
      const updatedData = { ...prev, [field]: processedValue };
      
      // Clear city when province changes
      if (field === 'province' && typeof processedValue === 'string' && processedValue !== prev.province) {
        updatedData.city = '';
        updatedData.barangay = ''; // Also clear barangay when province changes
        updatedData.zipCode = ''; // Clear zip code when province changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Clear barangay when city changes
      if (field === 'city' && typeof processedValue === 'string' && processedValue !== prev.city) {
        updatedData.barangay = '';
        updatedData.zipCode = ''; // Clear zip code when city changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Auto-populate zip code when barangay is selected
      if (field === 'barangay' && typeof processedValue === 'string') {
        if (processedValue && shouldAutoPopulateZipCode(processedValue, barangays)) {
          const zipCode = getZipCodeFromBarangay(barangays, processedValue);
          if (zipCode) {
            updatedData.zipCode = zipCode;
            setIsZipCodeAutoPopulated(true);
          } else {
            // If no zip code found, allow manual entry
            setIsZipCodeAutoPopulated(false);
          }
        } else {
          // If barangay is cleared, reset zip code and auto-population flag
          updatedData.zipCode = '';
          setIsZipCodeAutoPopulated(false);
        }
      }
      
      // Auto-calculate age when date of birth changes - UPDATED LOGIC
      if (field === 'dateOfBirth' && typeof processedValue === 'string' && processedValue) {
        const birthDate = dateUtils.parseDate(processedValue);
        if (birthDate) {
          const age = dateUtils.calculateAge(birthDate);
          updatedData.age = age.toString();
          
          // Show modal for ages 76-100, but only once per age (skip for Short Secure plans)
          if (!isShortSecure() && age >= 76 && age <= 100 && hasShownModalForAge !== age) {
            setShowPremiumModal(true);
            setHasShownModalForAge(age);
            
            // Use the enhanced getCurrentPremiumAmount function
            const currentPremiumAmount = getCurrentPremiumAmount();
            
            // Call PABA API to calculate adjusted premium
            try {
              calculatePremiumByAge({
                age: age,
                price: currentPremiumAmount
              });
            } catch (error) {
              console.error('GroupMemberModal: Failed to calculate premium adjustment:', error);
            }
          }
        }
      }
      
      return updatedData;
    });

    // Validate ID number after updating
    if (field === 'idNumber' && typeof processedValue === 'string' && formData.idType) {
      const validation = validateIDNumber(formData.idType, processedValue);
      setIdNumberError(validation.isValid ? null : validation.message || null);
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        
        // Also clear city and barangay errors when province changes
        if (field === 'province') {
          delete newErrors.city;
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Also clear barangay error when city changes
        if (field === 'city') {
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Clear zip code error when barangay changes
        if (field === 'barangay') {
          delete newErrors.zipCode;
        }
        
        return newErrors;
      });
    }
  };

  const handleMobileCountryCodeSelect = (countryCode: string) => {
    setFormData(prev => {
      const newData = { ...prev, countryCode };
      
      // Reformat mobile number with new country code
      if (prev.phoneNumber) {
        const includeCountryCode = countryCode !== '+63';
        const formatResult = autoFormatPhoneNumber(prev.phoneNumber, 'mobile', includeCountryCode);
        newData.formattedPhoneNumber = formatResult.formatted;
        newData.contactNumber = formatResult.formatted; // Update legacy field
      }
      
      return newData;
    });
    setIsMobileCountryDropdownOpen(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.phoneNumber;
      delete newErrors.contactNumber;
      return newErrors;
    });
  };

  const handleLandlineCountryCodeSelect = (countryCode: string) => {
    setFormData(prev => {
      const newData = { ...prev, landlineCountryCode: countryCode };
      
      // Reformat landline number with new country code
      if (prev.landlineNumber) {
        const includeCountryCode = countryCode !== '+63';
        const formatResult = autoFormatPhoneNumber(prev.landlineNumber, 'landline', includeCountryCode);
        newData.formattedLandlineNumber = formatResult.formatted;
      }
      
      return newData;
    });
    setIsLandlineCountryDropdownOpen(false);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.landlineNumber;
      return newErrors;
    });
  };

  // Input sanitization function to prevent non-phone characters
  const sanitizePhoneInput = (input: string): string => {
    return input.replace(/[^0-9\s\-()+ ]/g, '');
  };

  const handleMobileNumberChange = (value: string) => {
    // Allow only digits and + symbol for international format
    const sanitizedValue = value.replace(/[^0-9+]/g, '');
    
    // ✅ Dynamic character limit based on format
    let maxLength = 13; // Default to international format
    
    if (sanitizedValue.startsWith('09')) {
      maxLength = 11; // Local format: 09XXXXXXXXX
    } else if (sanitizedValue.startsWith('+63')) {
      maxLength = 13; // International format: +639XXXXXXXXX
    }
    
    // Check if input exceeds the appropriate limit
    if (sanitizedValue.length > maxLength) {
      return; // Stop updating if exceeded
    }

    // ✅ Update form state with raw value (no formatting)
    setFormData(prev => ({
      ...prev,
      phoneNumber: sanitizedValue,
      formattedPhoneNumber: sanitizedValue, // Store same value without formatting
      contactNumber: sanitizedValue // Legacy field
    }));

    // ✅ Clear validation errors if user is retyping
    if (errors.phoneNumber || errors.contactNumber) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        delete newErrors.contactNumber;
        return newErrors;
      });
    }
  };

  const handleLandlineNumberChange = (value: string) => {
    // Sanitize input first - remove any non-phone characters
    const sanitizedValue = sanitizePhoneInput(value);
    
    // Remove any existing formatting to get clean input
    const cleanValue = sanitizedValue.replace(/[\s\-()+]/g, '');
    
    const includeCountryCode = formData.landlineCountryCode !== '+63';
    const formatResult = autoFormatPhoneNumber(cleanValue, 'landline', includeCountryCode);

    if (formData.landlineCountryCode === '+63' && formatResult.formatted.length > 13) {
      return; // Stop updating if exceeded
    }
    
    setFormData(prev => ({
      ...prev,
      landlineNumber: cleanValue,
      formattedLandlineNumber: formatResult.formatted
    }));

    // Clear errors when user starts typing
    if (errors.landlineNumber) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.landlineNumber;
        return newErrors;
      });
    }
  };

  const handlePremiumModalClose = () => {
    setShowPremiumModal(false);
  };

  // Combine house/unit no and street name for the address field
  const getAddressValue = () => {
    const parts = [formData.houseUnitNo, formData.streetName].filter(Boolean);
    return parts.join(' ');
  };

  const handleAddressChange = (value: string) => {
    // For simplicity, we'll put the entire address in streetName
    setFormData(prev => ({
      ...prev,
      houseUnitNo: '',
      streetName: value
    }));

    // Clear address error
    if (errors.address) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.address;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = ' ';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.civilStatus || formData.civilStatus.trim() === '') {
      newErrors.civilStatus = 'Civil status is required';
    }

    if (!formData.occupation.trim()) {
      newErrors.occupation = 'Occupation is required';
    }

    // Address validation
    if ((!formData.houseUnitNo || !formData.houseUnitNo.trim()) && (!formData.streetName || !formData.streetName.trim())) {
      newErrors.address = 'House/Unit No. or Street Name is required';
    }

    if (!formData.barangay || !formData.barangay.trim()) {
      newErrors.barangay = 'Barangay is required';
    }

    if (!formData.city || !formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.province || !formData.province.trim()) {
      newErrors.province = 'Province is required';
    }

    if (!formData.zipCode || !formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.country === 'Philippines' && formData.zipCode && !isValidPhilippineZipCode(formData.zipCode)) {
      newErrors.zipCode = 'Please enter a valid 4-digit Philippine postal code';
    }

    if (!formData.country || !formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    // Mobile number validation (required)
    if (!formData.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Mobile number is required';
    } else {
      // Validate format: 09XXXXXXXXX (11 chars) or +639XXXXXXXXX (13 chars)
      const phoneNumber = formData.phoneNumber.trim();
      
      if (phoneNumber.startsWith('+639') && phoneNumber.length === 13) {
        // International format: +639XXXXXXXXX
        const digits = phoneNumber.substring(3); // Remove +63
        if (!/^9\d{9}$/.test(digits)) {
          newErrors.phoneNumber = 'Please enter a valid Philippine mobile number (+639XXXXXXXXX)';
        }
      } else if (phoneNumber.startsWith('09') && phoneNumber.length === 11) {
        // Local format: 09XXXXXXXXX
        if (!/^09\d{9}$/.test(phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid Philippine mobile number (09XXXXXXXXX)';
        }
      } else {
        newErrors.phoneNumber = 'Mobile number must be 11 digits (09XXXXXXXXX) or 13 characters (+639XXXXXXXXX)';
      }
    }

    // Landline number validation (optional, but if provided must be valid)
    if (formData.landlineNumber?.trim()) {
      const cleanNumber = getCleanPhoneNumber(formData.landlineNumber);
      const fullNumber = formData.landlineCountryCode === '+63' ? formData.landlineNumber : `${formData.landlineCountryCode}${cleanNumber}`;

      if (!validatePhoneNumber(fullNumber, 'landline')) {
        newErrors.landlineNumber = 'Please enter a valid Philippine landline number';
      }
    }

    // ID number validation
    if (!formData.idNumber.trim()) {
      newErrors.idNumber = 'ID number is required';
    } else if (!idValidationResult.isValid) {
      newErrors.idNumber = idValidationResult.message || 'Invalid ID number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    console.log('Form data before validation:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed, errors:', errors);
      return;
    }

    const memberData: GroupMember = {
      id: member?.id || generateUniqueId(),
      ...formData
    };

    // If this is a new member with premium adjustment, update the context with the actual member ID (skip for Short Secure)
    if (!isShortSecure() && !member?.id && adjustedPrice) {
      const currentAge = parseInt(formData.age);
      const currentPremiumAmount = getCurrentPremiumAmount();
      
      if (currentAge >= 76 && currentAge <= 100 && adjustedPrice !== currentPremiumAmount) {
        // Remove the temporary adjustment
        const tempMemberId = `temp-${formData.firstName}-${formData.lastName}-${currentAge}`;
        setGroupMemberAdjustment(tempMemberId, null);
        
        // Add the adjustment with the actual member ID
        console.log('GroupMemberModal: Updating premium adjustment with actual member ID:', memberData.id);
        setGroupMemberAdjustment(memberData.id, {
          memberId: memberData.id,
          originalPrice: currentPremiumAmount,
          adjustedPrice,
          age: currentAge
        });
      }
    }

    console.log('Saving member data:', memberData);
    onSave(memberData);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = () => {
    if (member?.id && onDelete) {
      onDelete(member.id);
      onClose();
    }
  };

  const addBeneficiary = () => {
    if ((formData.beneficiaries || []).length >= 3) {
      return;
    }

    const newBeneficiary: GroupMemberBeneficiary = {
      id: generateUniqueId(),
      title: 'Ms.',
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: 'N/A',
      relationship: 'Spouse'
    };

    setFormData(prev => ({
      ...prev,
      beneficiaries: [...(prev.beneficiaries || []), newBeneficiary]
    }));
  };

  const updateBeneficiary = (id: string, field: keyof GroupMemberBeneficiary, value: string) => {
    setFormData(prev => ({
      ...prev,
      beneficiaries: (prev.beneficiaries || []).map(ben =>
        ben.id === id ? { ...ben, [field]: value } : ben
      )
    }));
  };

  const removeBeneficiary = (id: string) => {
    setFormData(prev => ({
      ...prev,
      beneficiaries: (prev.beneficiaries || []).filter(ben => ben.id !== id)
    }));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="group-member-modal-backdrop" onClick={handleBackdropClick}>
      <div className="group-member-modal-container">
        {/* Modal Header */}
        <div className="group-member-modal-header">
          <div className="group-member-modal-title-section">
            <h2 className="group-member-modal-title">Member Information</h2>
            <p className="group-member-modal-subtitle">
              You can add up to 15 members
            </p>
          </div>
          <button
            className="group-member-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="group-member-modal-content">
          <div className="group-member-form">
            {/* Name Fields Row */}
            <div className="form-row name-row">
              <div className="form-group title-group">
                <label className="form-label">Title</label>
                <select
                  className={`form-select ${errors.title ? 'error' : ''}`}
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                >
                      <option value="">Title</option>                      
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Mr.">Mr.</option>
                </select>
                {errors.title && <span className="error-message">{errors.title}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  className={`form-input ${errors.firstName ? 'error' : ''}`}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder=""
                />
              </div>

              <div className="form-group">
                <label className="form-label">Middle Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  placeholder=""
                />
              </div>

              <div className="form-group">
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  className={`form-input ${errors.lastName ? 'error' : ''}`}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder=""
                />
              </div>

              <div className="form-group suffix-group">
                <label className="form-label">Suffix</label>
                <select
                  className="form-select"
                  value={formData.suffix}
                  onChange={(e) => handleInputChange('suffix', e.target.value)}
                >
                  <option value="N/A">N/A</option>
                  <option value="Jr.">Jr.</option>
                  <option value="Sr.">Sr.</option>
                  <option value="II">II</option>
                  <option value="III">III</option>
                </select>
              </div>
            </div>

            {/* Personal Information Row */}
            <div className="form-row personal-row form-row-personal-info-division">
              <div className="form-group">
                <label className="form-label group-member-date-of-birth-label">Date of Birth *</label>
                <DatePicker
                  value={formData.dateOfBirth}
                  onChange={(value) => handleInputChange('dateOfBirth', value)}
                  placeholder="MM/DD/YYYY"
                  name="dateOfBirth"
                  className={`form-datepicker group-member-modal-date-picker-wrapper ${errors.dateOfBirth ? 'error' : ''}`}
                  position="below"
                  validationRules={birthdateValidationRules}
                  error={errors.dateOfBirth}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Age *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.age}
                  readOnly
                  placeholder="Auto-calculated"
                />
              </div>

              <div className="form-group group-gender-position">
                <label className="form-label">Gender *</label>
                <select
                  className={`form-select ${errors.gender ? 'error' : ''}`}
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                >
                  <option value="">Choose Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
            {/* Civil Status and Occupation */}            
            <div className="form-row id-row">
              <div className="form-group">
                <label className="form-label">Civil Status *</label>
                <select
                  className="form-select"
                  value={formData.civilStatus}
                  onChange={(e) => handleInputChange('civilStatus', e.target.value)}
                >
                  <option value="">Choose Civil Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Separated/Divorced">Separated/Divorced</option>
                  <option value="Widow/Widower">Widow/Widower</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Occupation *</label>
                <input
                  type="text"
                  className={`form-input ${errors.occupation ? 'error' : ''}`}
                  value={formData.occupation}
                  onChange={(e) => handleInputChange('occupation', e.target.value)}
                  placeholder=""
                />
              </div>
            </div>

            {/* ID Information Row - FIXED: Properly structured for 2-column layout */}
            <div className="form-row id-row">
              <div className="form-group">
                <label className="form-label">ID Type *</label>
                <SearchableDropdown
                  options={idTypesOptions
                  .filter((idType) => {
                    return formData.nationality === "Filipino" || formData.nationality === ""
                      ? idType.isNonFilipino === false
                      : idType.isNonFilipino === true;
                  })
                  .map((idType) => idType.name)}
                  selectedValue={formData.idType}
                  onChange={(value) => handleInputChange('idType', value)}
                  placeholder={idTypesLoading ? "Loading ID Types..." : "Choose ID Type"}
                  searchPlaceholder="Search ID types..."
                  disabled={idTypesLoading || !!idTypesError}
                  dropdownId="id-type-dropdown"
                />
              </div>

              <div className="form-group">
                <label className="form-label">ID Number *</label>
                <input
                  type="text"
                  className={`form-input ${errors.idNumber || idNumberError ? 'error' : ''} ${formData.idNumber && idValidationResult.isValid ? 'valid' : ''}`}
                  value={formData.idNumber}
                  onChange={(e) => handleInputChange('idNumber', e.target.value)}
                  placeholder={idFormatInfo?.example || 'Enter ID number'}
                />
              </div>
            </div>

            {/* Address Information Section */}
            <div className="address-information-section">
              <h3 className="section-title">Address Information</h3>
              
              {/* Address Sharing Checkbox - Show for every member */}
              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.shareAddressWithGroupMembers}
                      onChange={(e) => handleInputChange('shareAddressWithGroupMembers', e.target.checked)}
                      className="checkbox-input"
                      disabled={false}
                    />
                    <span className="group-member-checkbox-text">Do you and your group members share the same home address?</span>
                  </label>
                </div>
              </div>

              {/* First Row: Address and Barangay (2 columns) */}
              <div className="form-row address-row-1">
                <div className="form-group address-group">
                  <label className="form-label">House/Unit No., Street Name, Subdivision/Village *</label>
                  <input
                    type="text"
                    className={`form-input ${errors.address ? 'error' : ''}`}
                    value={getAddressValue()}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    placeholder=""
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Province *</label>
                  <SearchableDropdown
                    options={provinceOptions}
                    selectedValue={formData.province || ''}
                    onChange={(value) => handleInputChange('province', value)}
                    placeholder={provincesLoading ? "Loading provinces..." : "Select Province"}
                    searchPlaceholder="Search provinces..."
                    className={errors.province ? 'error' : ''}
                    error={!!errors.province}
                    position="below"
                    dropdownId="group-member-province"
                    disabled={provincesLoading || !!provincesError}
                  />
                </div>
              </div>

              {/* Second Row: City, Barangay, Zip Code, Country (4 columns) */}
              <div className="form-row address-row-2">
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <SearchableDropdown
                    options={cityOptions}
                    selectedValue={formData.city || ''}
                    onChange={(value) => handleInputChange('city', value)}
                    placeholder={
                      !formData.province
                        ? "Select City"
                        : citiesLoading
                          ? "Loading cities..."
                          : "Select City"
                    }
                    searchPlaceholder="Search cities..."
                    className={errors.city ? 'error' : ''}
                    error={!!errors.city}
                    position="below"
                    dropdownId="group-member-city"
                    disabled={!formData.province || citiesLoading || !!citiesError}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Barangay *</label>
                  <SearchableDropdown
                    options={barangayOptions}
                    selectedValue={formData.barangay || ''}
                    onChange={(value) => handleInputChange('barangay', value)}
                    placeholder={
                      !formData.city
                        ? "Select Barangay"
                        : barangaysLoading
                          ? "Loading barangays..."
                          : "Select Barangay"
                    }
                    searchPlaceholder="Search barangays..."
                    className={errors.barangay ? 'error' : ''}
                    error={!!errors.barangay}
                    position="below"
                    dropdownId="group-member-barangay"
                    disabled={!formData.city || barangaysLoading || !!barangaysError}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Zip Code *</label>
                  <input
                    type="text"
                    className={`form-input ${errors.zipCode ? 'error' : ''} ${isZipCodeAutoPopulated ? 'auto-populated' : ''}`}
                    value={formData.zipCode || ''}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    placeholder={isZipCodeAutoPopulated ? "Auto-populated from barangay" : "Zip Code"}
                    maxLength={4}
                    readOnly={isZipCodeAutoPopulated}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Country *</label>
                  <SearchableDropdown
                    options={['Philippines']}
                    selectedValue="Philippines"
                    onChange={() => {}} // No-op function since it's disabled
                    placeholder="Philippines"
                    searchPlaceholder="Philippines"
                    className={`${errors.country ? 'error' : ''} disabled-country-field`}
                    error={!!errors.country}
                    position="below"
                    dropdownId="group-member-country"
                    disabled={true} // Always disabled
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="contact-information-section">
              <h3 className="section-title">Contact Information</h3>
              <p className="section-description">
                Provide a mobile number where we can reach the Principal Insured.
              </p>

              {/* Mobile Number Input */}
            <div className='group-member-contact-information-form'>              
              <div className="form-group">
                <label className="form-label">
                  Mobile Number *
                </label>
                
                <div className={`phone-input-container ${errors.phoneNumber ? 'error' : ''}`}>
                  {/* Country Code Dropdown */}
                  <div
                    className={`country-code-dropdown ${isMobileCountryDropdownOpen ? 'open' : ''}`}
                    ref={mobileCountryDropdownRef}
                  >
                    <button
                      type="button"
                      className="country-code-trigger"
                      onClick={() => setIsMobileCountryDropdownOpen(!isMobileCountryDropdownOpen)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsMobileCountryDropdownOpen(!isMobileCountryDropdownOpen);
                        }
                      }}
                    >
                      <span>{currentMobileCountryCode.code}</span>
                      <svg
                        className="dropdown-arrow"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isMobileCountryDropdownOpen && (
                      <div className="country-code-options">
                        {COUNTRY_CODES.map((country) => (
                          <div
                            key={country.code}
                            className={`country-code-option ${country.value === formData.countryCode ? 'selected' : ''}`}
                            onClick={() => handleMobileCountryCodeSelect(country.value)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleMobileCountryCodeSelect(country.value);
                              }
                            }}
                          >
                            {country.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Mobile Number Input */}
                  <input
                    ref={mobileInputRef}
                    type="tel"
                    pattern="[0-9\s\-()+ ]*"
                    className="phone-number-input"
                    value={formData.formattedPhoneNumber}
                    onChange={(e) => handleMobileNumberChange(e.target.value)}
                    placeholder="09123456789 or +639123456789"
                  />
                </div>
              </div>

              {/* Landline Number Input (Optional) */}
              <div className="form-group">
                <label className="form-label">Landline Number</label>
                
                <div className={`phone-input-container ${errors.landlineNumber ? 'error' : ''}`}>
                  {/* Country Code Dropdown */}
                  <div
                    className={`country-code-dropdown ${isLandlineCountryDropdownOpen ? 'open' : ''}`}
                    ref={landlineCountryDropdownRef}
                  >
                    <button
                      type="button"
                      className="country-code-trigger"
                      onClick={() => setIsLandlineCountryDropdownOpen(!isLandlineCountryDropdownOpen)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setIsLandlineCountryDropdownOpen(!isLandlineCountryDropdownOpen);
                        }
                      }}
                    >
                      <span>{currentLandlineCountryCode.code}</span>
                      <svg
                        className="dropdown-arrow"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isLandlineCountryDropdownOpen && (
                      <div className="country-code-options">
                        {COUNTRY_CODES.map((country) => (
                          <div
                            key={country.code}
                            className={`country-code-option ${country.value === formData.landlineCountryCode ? 'selected' : ''}`}
                            onClick={() => handleLandlineCountryCodeSelect(country.value)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleLandlineCountryCodeSelect(country.value);
                              }
                            }}
                          >
                            {country.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Landline Number Input */}
                  <input
                    ref={landlineInputRef}
                    type="tel"
                    pattern="[0-9\s\-()+ ]*"
                    className="phone-number-input"
                    value={formData.formattedLandlineNumber}
                    onChange={(e) => handleLandlineNumberChange(e.target.value)}
                    placeholder="(02) 8230-8511"
                  />
                </div>
              </div>
            </div>
            </div>

            {/* Beneficiary Information Section */}
            <div className="beneficiary-information-section">
              <div className="beneficiary-header">
                <div>
                  <h3 className="section-title">Beneficiary Information</h3>
                  <p className="section-description">
                    List up to 3 individuals who will receive benefits from this travel insurance policy in case of a claim.
                  </p>
                </div>
                {(formData.beneficiaries || []).length < 3 && (
                  <button
                    type="button"
                    className="add-beneficiary-btn"
                    onClick={addBeneficiary}
                    aria-label="Add beneficiary"
                  >
                    +
                  </button>   
                )}
              </div>

              {(formData.beneficiaries || []).map((beneficiary, index) => (
                <div key={beneficiary.id} className="beneficiary-form group-member-beneficiary-form">
                  <div className="beneficiary-form-header">
                    <h4 className="beneficiary-title">Beneficiary {index + 1}</h4>
                    <button
                      type="button"
                      className="remove-beneficiary-btn"
                      onClick={() => removeBeneficiary(beneficiary.id)}
                      aria-label={`Remove beneficiary ${index + 1}`}
                    >
                      ×
                    </button>
                  </div>

                  <div className="form-row beneficiary-name-row">
                    <div className="form-group title-group">
                      <label className="form-label">Title</label>
                      <select
                        className="form-select"
                        value={beneficiary.title}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'title', e.target.value)}
                      >
                        <option value=''>Title</option>
                        <option value="Ms.">Ms.</option>
                        <option value="Mrs.">Mrs.</option>
                        <option value="Mr.">Mr.</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">First Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={beneficiary.firstName}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'firstName', e.target.value)}
                        placeholder=""
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Middle Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={beneficiary.middleName || ''}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'middleName', e.target.value)}
                        placeholder=""
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={beneficiary.lastName}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'lastName', e.target.value)}
                        placeholder=""
                      />
                    </div>

                    <div className="form-group suffix-group">
                      <label className="form-label">Suffix</label>
                      <select
                        className="form-select"
                        value={beneficiary.suffix}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'suffix', e.target.value)}
                      >
                        <option value="N/A">N/A</option>
                        <option value="Jr.">Jr.</option>
                        <option value="Sr.">Sr.</option>
                        <option value="II">II</option>
                        <option value="III">III</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group relationship-group">
                      <label className="form-label">Relationship to this group member *</label>
                      <select
                        className="form-select"
                        value={beneficiary.relationship}
                        onChange={(e) => updateBeneficiary(beneficiary.id, 'relationship', e.target.value)}
                        disabled={relationshipsLoading}
                      >
                        <option value="">
                          {relationshipsLoading ? 'Loading relationships...' : 'Choose Relationships'}
                        </option>
                        {relationshipsError && (
                          <option value="" disabled>
                            Error loading relationships
                          </option>
                        )}
                        {relationshipsOptions.map((relationship) => (
                          <option key={relationship} value={relationship}>
                            {relationship}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Error messages for loading states */}
            {relationshipsError && (
              <div className="error-message">
                <p>Unable to load relationships. Please refresh the page or try again later.</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className={`group-member-modal-footer ${showDeleteButton ? 'has-delete' : 'no-delete'}`}>
          {/* Delete button - only show in edit mode */}
          {showDeleteButton && (
            <div className="group-member-modal-footer-left">
              <button
                className="group-member-modal-btn-delete"
                onClick={handleDelete}
                aria-label="Delete group member"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
                Delete Group Member
              </button>
              <p className="delete-warning-text">
                By clicking "Delete Group Member" it will permanently remove this group member.
              </p>
            </div>
          )}
          
          <div className="group-member-modal-footer-actions">
            <button
              className="group-member-modal-btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              className={`group-member-modal-btn-save ${!hasFormChanged ? 'disabled' : ''}`}
              onClick={handleSave}
              disabled={!hasFormChanged}
            >
              Save
            </button>
          </div>
        </div>

        {/* Premium Update Modal */}
        <PremiumUpdateModal
          isOpen={showPremiumModal}
          onClose={handlePremiumModalClose}
          adjustedPrice={adjustedPrice}
          pabaLoading={pabaLoading}
        />
      </div>
    </div>
  );

  // Render modal using React portal to ensure it's rendered at document body level
  return createPortal(modalContent, document.body);
};

export default GroupMemberModal;